<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { PageItem } from '@/db/content';
import { getRootPathsAndDefaultLocale, getUrlForItem } from '@/navTree';
import { useContentStore } from '@/stores/content';
import type { ApiImage } from '@/types/images';
import { isOptimisedImage } from '@/types/images';
import { stripHtmlToText, normalizeWagtailRichTextHtml } from '@/utils/html';
import { getReadingTimeForPageContent } from '@/utils/pageContentPlainText';
import { useIndmoRichTextIcons } from '@/composables/useIndmoRichTextIcons';
import { onOfflineMediaImgError } from '@/utils/offlineMediaPlaceholder';
import type { ContentBlockItem } from '@/components/contentBlocks';

const props = defineProps<{
  article: PageItem;
}>();

const contentStore = useContentStore();
const { t, locale } = useI18n();

const listingItems = computed(() => contentStore.expandedListingItems);
const navRoots = computed(() => getRootPathsAndDefaultLocale(listingItems.value));

const cardRoot = ref<HTMLElement | null>(null);
const readingMinutes = ref<number | null>(null);
let readingTimeLoadGeneration = 0;

useIndmoRichTextIcons(cardRoot, () => normalizeWagtailRichTextHtml(props.article.rich_title));

const articleHref = computed(() =>
  getUrlForItem(
    props.article,
    navRoots.value.rootPathByLocale,
    navRoots.value.defaultLocale
  )
);

function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

function imageUrl(item: PageItem): string | undefined {
  const image = (item as { image?: ApiImage }).image;
  if (!image) return undefined;
  if (isOptimisedImage(image)) return image.fallback?.url ?? undefined;
  return 'url' in image ? (image as { url?: string }).url ?? undefined : undefined;
}

function imageAlt(item: PageItem): string {
  const image = (item as { image?: ApiImage }).image;
  const richPlain = stripHtmlToText(item.rich_title ?? '');
  return (image?.title ?? (richPlain || item.title || '')) as string;
}

watch(
  [() => props.article.id, () => locale.value],
  async () => {
    const id = props.article.id;
    const gen = ++readingTimeLoadGeneration;
    readingMinutes.value = null;
    if (typeof id !== 'number') return;
    const detail = await contentStore.getPageDetail(id);
    if (gen !== readingTimeLoadGeneration || !detail) return;
    const raw = (detail as { content?: ContentBlockItem[] }).content;
    readingMinutes.value = Array.isArray(raw)
      ? getReadingTimeForPageContent(String(locale.value), raw)
      : 0;
  },
  { immediate: true }
);
</script>

<template>
  <article ref="cardRoot" class="featured-news-card">
    <a :href="articleHref" class="featured-news-card__link">
      <div class="featured-news-card__media">
        <img
          v-if="imageUrl(article)"
          :src="imageUrl(article) ?? ''"
          :alt="imageAlt(article)"
          class="featured-news-card__media-img"
          loading="lazy"
          width="1080"
          height="720"
          @error="onOfflineMediaImgError"
        />
        <div
          v-else
          class="featured-news-card__media-placeholder"
          aria-hidden="true"
        />

        <span v-if="article.category?.name" class="featured-news-card__badge">
          <svg
            class="featured-news-card__badge-icon"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M7 7h.01"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ article.category.name }}
        </span>
      </div>

      <div class="featured-news-card__content">
        <div class="featured-news-card__eyebrow">
          <span class="featured-news-card__eyebrow-pill">
            {{ t('news.featured') }}
          </span>
        </div>

        <div
          v-if="article.rich_title && String(article.rich_title).trim()"
          class="featured-news-card__title featured-news-card__title--rich block-rich-text"
          v-html="normalizeWagtailRichTextHtml(article.rich_title)"
        />
        <div v-else class="featured-news-card__title">
          {{ article.title }}
        </div>

        <p
          v-if="article.meta?.search_description"
          class="featured-news-card__description"
        >
          {{ article.meta.search_description }}
        </p>

        <div
          v-if="article.date || readingMinutes != null"
          class="featured-news-card__meta"
        >
          <span v-if="article.date" class="featured-news-card__meta-item">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <rect
                x="3"
                y="4"
                width="18"
                height="18"
                rx="2"
                stroke="currentColor"
                stroke-width="2"
              />
              <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" stroke-width="2" />
            </svg>
            <time :datetime="article.date">{{ formatDate(article.date) }}</time>
          </span>
          <span
            v-if="readingMinutes != null"
            class="featured-news-card__meta-item"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
              <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            {{ t('news.readingMinutes', { n: readingMinutes }) }}
          </span>
        </div>

        <span class="featured-news-card__cta">
          {{ t('news.readMore') }}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <path d="m12 5 7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </div>
    </a>
  </article>
</template>

<style scoped>
.featured-news-card {
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 1rem;
  overflow: hidden;
  transition:
    box-shadow 0.3s ease,
    transform 0.3s ease;
}

.featured-news-card:hover {
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
  transform: translateY(-0.25rem);
}

.featured-news-card__link {
  display: grid;
  grid-template-columns: 1fr;
  text-decoration: none;
  color: inherit;
}

@media (min-width: 768px) {
  .featured-news-card__link {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}

.featured-news-card__media {
  position: relative;
  min-height: 11rem;
  background: var(--color-bg-muted, #f3f4f6);
}

@media (min-width: 768px) {
  .featured-news-card__media {
    min-height: 100%;
  }
}

.featured-news-card__media-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.featured-news-card__media-placeholder {
  width: 100%;
  height: 100%;
  min-height: 11rem;
  background: linear-gradient(
    135deg,
    var(--color-bg-muted, #f3f4f6) 0%,
    #e5e7eb 100%
  );
}

.featured-news-card__badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 0.5rem;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
}

.featured-news-card__badge-icon {
  flex-shrink: 0;
}

.featured-news-card__content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1.5rem;
}

@media (min-width: 768px) {
  .featured-news-card__content {
    padding: 2rem;
  }
}

.featured-news-card__eyebrow {
  margin-bottom: 0.75rem;
}

.featured-news-card__eyebrow-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border: 1px solid color-mix(in srgb, var(--color-primary, #047857) 20%, transparent);
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--color-primary, #047857) 10%, white);
  color: var(--color-primary, #047857);
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
}

.featured-news-card__title {
  margin: 0 0 0.75rem;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.25;
  transition: color 0.2s ease;
}

@media (min-width: 768px) {
  .featured-news-card__title {
    font-size: 1.875rem;
  }
}

.featured-news-card__link:hover .featured-news-card__title {
  color: var(--color-primary, #047857);
}

.featured-news-card__title--rich.block-rich-text :deep(.indmo-richtext-primary) {
  color: var(--color-primary);
}

.featured-news-card__title--rich.block-rich-text :deep(.indmo-richtext-icon) {
  display: inline-flex;
  vertical-align: -0.125em;
  margin-inline-end: 0.15em;
  color: var(--color-primary);
}

.featured-news-card__title--rich.block-rich-text :deep(.indmo-richtext-icon svg) {
  width: 1em;
  height: 1em;
}

.featured-news-card__title--rich.block-rich-text :deep(p) {
  margin: 0;
}

.featured-news-card__description {
  margin: 0 0 1rem;
  color: var(--color-text-muted, #525252);
  font-size: 0.875rem;
  line-height: 1.5;
}

@media (min-width: 768px) {
  .featured-news-card__description {
    font-size: 1rem;
  }
}

.featured-news-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  margin-bottom: 1rem;
  color: var(--color-text-muted, #525252);
  font-size: 0.75rem;
  line-height: 1.4;
}

@media (min-width: 768px) {
  .featured-news-card__meta {
    font-size: 0.875rem;
  }
}

.featured-news-card__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.featured-news-card__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-primary, #047857);
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.4;
}
</style>
